import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { loadAccess, MenuAction, MenuEntry } from '../../../auth/access';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import { AccessRole, accessApi, AccessUser } from '../access.api';

const ACTIONS: MenuAction[] = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'APPROVE', 'EXPORT'];
const ACTION_LABEL: Record<MenuAction, string> = {
  VIEW: 'View',
  CREATE: 'Create',
  EDIT: 'Edit',
  DELETE: 'Delete',
  APPROVE: 'Approve',
  EXPORT: 'Export',
};

/** menu code → granted actions; a menu with no entry is not granted. */
type Draft = Record<string, MenuAction[]>;

interface Module {
  code: string;
  label: string;
  icon: string;
  menus: MenuEntry[];
}

const fail = (e: unknown, message: string, code: string) => toast.error(logError({ message, error: e, code }));

/**
 * Administration → Roles & Menu Access (V30). Pick a role on the left; on the right tick the
 * sub-menus it sees and, for each, the actions it may use (only the actions a page supports can
 * be ticked). The Users tab gives logins the role. Admin always has everything; admin-only
 * entries (Users, Roles & Menu Access, Books Settings) stay with admin.
 */
export default function RolesPage() {
  const [menus, setMenus] = useState<MenuEntry[]>([]);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string>('');
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [roleDialog, setRoleDialog] = useState<null | { mode: 'new' | 'edit'; code: string; label: string; description: string }>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (keep?: string) => {
    setLoading(true);
    try {
      const [m, r, u] = await Promise.all([accessApi.menus(), accessApi.roles(), accessApi.users()]);
      setMenus(m);
      setRoles(r);
      setUsers(u);
      setSelected((cur) => keep ?? (cur || r.find((x) => x.code !== 'admin')?.code || r[0]?.code || ''));
    } catch (e) {
      fail(e, 'Could not load roles and menus', 'ACCESS_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const role = roles.find((r) => r.code === selected) ?? null;
  const isAdminRole = role?.code === 'admin';

  // Reset the draft whenever another role is picked or the saved grants change.
  // (keyed on the grants object, which a user-count change keeps, so assigning a login keeps edits)
  const roleCode = role?.code;
  const roleGrants = role?.grants;
  useEffect(() => {
    setDraft(roleGrants ? { ...roleGrants } : {});
  }, [roleCode, roleGrants]);

  const modules = useMemo(() => {
    const out: Module[] = [];
    menus.forEach((m) => {
      let mod = out.find((x) => x.code === m.moduleCode);
      if (!mod) {
        mod = { code: m.moduleCode, label: m.moduleLabel, icon: m.moduleIcon, menus: [] };
        out.push(mod);
      }
      mod.menus.push(m);
    });
    return out;
  }, [menus]);

  const dirty = useMemo(() => {
    if (!role) return false;
    const norm = (d: Draft) =>
      JSON.stringify(
        Object.keys(d)
          .sort()
          .map((k) => [k, [...d[k]].sort()])
      );
    return norm(draft) !== norm(role.grants);
  }, [draft, role]);

  const granted = (code: string, action: MenuAction) => isAdminRole || (draft[code]?.includes(action) ?? false);

  /** Ticking an action ticks View; unticking View drops the sub-menu altogether. */
  const toggle = (menu: MenuEntry, action: MenuAction) => {
    setDraft((d) => {
      const next = { ...d };
      const cur = next[menu.code] ?? [];
      if (action === 'VIEW') {
        if (cur.length) delete next[menu.code];
        else next[menu.code] = ['VIEW'];
      } else if (cur.includes(action)) {
        next[menu.code] = cur.filter((a) => a !== action);
      } else {
        next[menu.code] = Array.from(new Set<MenuAction>(['VIEW', ...cur, action]));
      }
      return next;
    });
  };

  /** The module's row toggle: everything it supports on, or the whole module off. */
  const toggleModule = (mod: Module) => {
    const assignable = mod.menus.filter((m) => !m.adminOnly);
    const allOn = assignable.every((m) => m.actions.every((a) => draft[m.code]?.includes(a)));
    setDraft((d) => {
      const next = { ...d };
      assignable.forEach((m) => {
        if (allOn) delete next[m.code];
        else next[m.code] = [...m.actions];
      });
      return next;
    });
  };

  const copyFrom = (code: string) => {
    const src = roles.find((r) => r.code === code);
    if (src) setDraft({ ...src.grants });
  };

  const save = async () => {
    if (!role) return;
    setSaving(true);
    try {
      const grants = Object.entries(draft).map(([menuCode, actions]) => ({ menuCode, actions: actions as MenuAction[] }));
      const saved = await accessApi.saveGrants(role.code, grants);
      setRoles((rs) => rs.map((r) => (r.code === saved.code ? saved : r)));
      toast.success(`Menu access for ${saved.label} saved.`);
      loadAccess(true).catch(() => undefined);
    } catch (e) {
      fail(e, 'Could not save the menu access', 'ACCESS_SAVE_FAILED');
    } finally {
      setSaving(false);
    }
  };

  const saveRole = async () => {
    if (!roleDialog) return;
    try {
      const body = { label: roleDialog.label.trim(), description: roleDialog.description.trim() || null };
      const saved =
        roleDialog.mode === 'new'
          ? await accessApi.createRole({ code: roleDialog.code.trim(), ...body })
          : await accessApi.updateRole(roleDialog.code, body);
      toast.success(roleDialog.mode === 'new' ? `Role ${saved.label} created.` : 'Role saved.');
      setRoleDialog(null);
      await load(saved.code);
    } catch (e) {
      fail(e, 'Could not save the role', 'ROLE_SAVE_FAILED');
    }
  };

  const deleteRole = async () => {
    if (!role) return;
    try {
      await accessApi.deleteRole(role.code);
      toast.success(`Role ${role.label} deleted.`);
      setDeleting(false);
      setSelected('');
      await load();
    } catch (e) {
      fail(e, 'Could not delete the role', 'ROLE_DELETE_FAILED');
    }
  };

  const assign = async (user: AccessUser, code: string | null) => {
    try {
      const saved = await accessApi.assignRole(user.id, code);
      setUsers((us) => us.map((u) => (u.id === saved.id ? saved : u)));
      setRoles((rs) =>
        rs.map((r) => ({
          ...r,
          userCount: r.userCount + (r.code === saved.role ? 1 : 0) - (r.code === user.role ? 1 : 0),
        }))
      );
      toast.success(`${saved.name} is now ${roles.find((r) => r.code === saved.role)?.label ?? 'without a role'}.`);
    } catch (e) {
      fail(e, 'Could not change the role', 'ROLE_ASSIGN_FAILED');
    }
  };

  const members = users.filter((u) => u.role === selected);
  const others = users.filter((u) => u.role !== selected);
  const noRole = users.filter((u) => !u.role || !roles.some((r) => r.code === u.role));

  return (
    <Stack gap={3}>
      <SectionTitle
        title='ROLES & MENU ACCESS'
        action={
          <Button
            variant='contained'
            startIcon={<Icon icon='mdi:plus' />}
            onClick={() => setRoleDialog({ mode: 'new', code: '', label: '', description: '' })}
          >
            New role
          </Button>
        }
      />
      {loading && <LinearProgress />}
      {noRole.length > 0 && (
        <Alert severity='warning'>
          {noRole.length} login{noRole.length > 1 ? 's have' : ' has'} no role and see{noRole.length > 1 ? '' : 's'} no menu:{' '}
          {noRole.map((u) => u.name).join(', ')}. Give them a role on a role's Users tab.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card title='ROLES'>
            <List dense disablePadding>
              {roles.map((r) => (
                <ListItemButton key={r.code} selected={r.code === selected} onClick={() => setSelected(r.code)} sx={{ borderRadius: 1 }}>
                  <ListItemText
                    primary={
                      <Stack direction='row' gap={1} alignItems='center'>
                        <Typography fontWeight={600}>{r.label}</Typography>
                        {r.code === 'admin' && <Icon icon='mdi:shield-crown-outline' />}
                      </Stack>
                    }
                    secondary={`${r.userCount} user${r.userCount === 1 ? '' : 's'} · ${r.code === 'admin' ? 'everything' : `${Object.keys(r.grants).length} menus`}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          {role && (
            <Card
              title={role.label.toUpperCase()}
              action={
                <Stack direction='row' gap={1}>
                  <Tooltip title='Rename / describe'>
                    <IconButton
                      onClick={() => setRoleDialog({ mode: 'edit', code: role.code, label: role.label, description: role.description ?? '' })}
                    >
                      <Icon icon='mdi:pencil-outline' />
                    </IconButton>
                  </Tooltip>
                  {!role.system && (
                    <Tooltip title='Delete role'>
                      <IconButton color='error' onClick={() => setDeleting(true)}>
                        <Icon icon='mdi:delete-outline' />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
            >
              <Stack gap={2}>
                {role.description && <Typography color='text.secondary'>{role.description}</Typography>}
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label='Menu access' />
                  <Tab label={`Users (${members.length})`} />
                </Tabs>

                {tab === 0 && (
                  <Stack gap={2}>
                    {isAdminRole ? (
                      <Alert severity='info'>Admin always has every menu and every action. It cannot be changed.</Alert>
                    ) : (
                      <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
                        <TextField
                          select
                          size='small'
                          label='Copy from role'
                          value=''
                          onChange={(e) => copyFrom(e.target.value)}
                          sx={{ minWidth: 220 }}
                        >
                          {roles
                            .filter((r) => r.code !== role.code && r.code !== 'admin')
                            .map((r) => (
                              <MenuItem key={r.code} value={r.code}>
                                {r.label}
                              </MenuItem>
                            ))}
                        </TextField>
                        <Box sx={{ flex: 1 }} />
                        {dirty && <Chip color='warning' size='small' label='Unsaved changes' />}
                        <Button disabled={!dirty || saving} onClick={() => setDraft({ ...role.grants })}>
                          Undo
                        </Button>
                        <Button variant='contained' disabled={!dirty || saving} onClick={save} startIcon={<Icon icon='mdi:content-save-outline' />}>
                          {saving ? 'Saving…' : 'Save'}
                        </Button>
                      </Stack>
                    )}

                    <TableContainer sx={{ maxHeight: '65vh' }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Main menu / sub-menu</TableCell>
                            {ACTIONS.map((a) => (
                              <TableCell key={a} align='center'>
                                {ACTION_LABEL[a]}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modules.map((mod) => {
                            const assignable = mod.menus.filter((m) => !m.adminOnly);
                            const on = assignable.filter((m) => granted(m.code, 'VIEW')).length;
                            return [
                              <TableRow key={mod.code} sx={{ bgcolor: 'action.hover' }}>
                                <TableCell {...({ colSpan: ACTIONS.length + 1 } as object)}>
                                  <Stack direction='row' alignItems='center' gap={1}>
                                    <Checkbox
                                      size='small'
                                      disabled={isAdminRole || assignable.length === 0}
                                      checked={isAdminRole || (assignable.length > 0 && on === assignable.length)}
                                      indeterminate={!isAdminRole && on > 0 && on < assignable.length}
                                      onChange={() => toggleModule(mod)}
                                    />
                                    <Icon icon={mod.icon} />
                                    <Typography fontWeight={700}>{mod.label}</Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                      {isAdminRole ? 'all' : `${on} of ${assignable.length}`}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                              </TableRow>,
                              ...mod.menus.map((m) => (
                                <TableRow key={m.code} hover>
                                  <TableCell sx={{ pl: 7 }}>
                                    <Stack direction='row' alignItems='center' gap={1}>
                                      <Icon icon={m.icon} width={16} />
                                      <Box>
                                        <Typography variant='body2' fontWeight={500}>
                                          {m.label}
                                          {m.adminOnly && <Chip size='small' label='Admin only' sx={{ ml: 1 }} />}
                                        </Typography>
                                        {m.description && (
                                          <Typography variant='caption' color='text.secondary'>
                                            {m.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Stack>
                                  </TableCell>
                                  {ACTIONS.map((a) => (
                                    <TableCell key={a} align='center' padding='checkbox'>
                                      {m.actions.includes(a) ? (
                                        <Checkbox
                                          size='small'
                                          disabled={isAdminRole || m.adminOnly}
                                          checked={!m.adminOnly && granted(m.code, a)}
                                          onChange={() => toggle(m, a)}
                                        />
                                      ) : (
                                        <Typography color='text.disabled'>–</Typography>
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              )),
                            ];
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                )}

                {tab === 1 && (
                  <Stack gap={2}>
                    <Autocomplete
                      options={others}
                      getOptionLabel={(u) => `${u.name} (${u.username})${u.role ? ` — ${roles.find((r) => r.code === u.role)?.label ?? u.role}` : ' — no role'}`}
                      onChange={(_, u) => u && assign(u, role.code)}
                      value={null}
                      renderInput={(params) => <TextField {...params} size='small' label={`Add a login to ${role.label}`} />}
                    />
                    {members.length === 0 && <Typography color='text.secondary'>No login has this role.</Typography>}
                    <Table size='small'>
                      <TableBody>
                        {members.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <Typography fontWeight={500}>{u.name}</Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {u.username}
                              </Typography>
                            </TableCell>
                            <TableCell align='right' sx={{ width: 260 }}>
                              <TextField
                                select
                                size='small'
                                fullWidth
                                label='Role'
                                value={u.role ?? ''}
                                onChange={(e) => assign(u, e.target.value || null)}
                              >
                                {roles.map((r) => (
                                  <MenuItem key={r.code} value={r.code}>
                                    {r.label}
                                  </MenuItem>
                                ))}
                                <MenuItem value=''>
                                  <em>No role</em>
                                </MenuItem>
                              </TextField>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                )}
              </Stack>
            </Card>
          )}
        </Grid>
      </Grid>

      <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} fullWidth maxWidth='xs'>
        <DialogTitle>{roleDialog?.mode === 'new' ? 'New role' : 'Edit role'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <TextField
              label='Name'
              value={roleDialog?.label ?? ''}
              onChange={(e) =>
                setRoleDialog((d) =>
                  d && {
                    ...d,
                    label: e.target.value,
                    // Suggest the code from the name while it is new and untouched.
                    code:
                      d.mode === 'new' && (d.code === '' || d.code === slug(d.label)) ? slug(e.target.value) : d.code,
                  }
                )
              }
              autoFocus
              required
            />
            <TextField
              label='Code'
              value={roleDialog?.code ?? ''}
              disabled={roleDialog?.mode === 'edit'}
              onChange={(e) => setRoleDialog((d) => d && { ...d, code: e.target.value.toLowerCase() })}
              helperText='Lower-case letters, digits and _. Stored on each login; cannot change later.'
              required
            />
            <TextField
              label='Description'
              value={roleDialog?.description ?? ''}
              onChange={(e) => setRoleDialog((d) => d && { ...d, description: e.target.value })}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(null)}>Cancel</Button>
          <Button variant='contained' onClick={saveRole} disabled={!roleDialog?.label.trim() || !roleDialog?.code.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleting} onClose={() => setDeleting(false)}>
        <DialogTitle>Delete role {role?.label}?</DialogTitle>
        <DialogContent>
          <Typography>Its menu access goes with it. A role that still has logins cannot be deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(false)}>Keep</Button>
          <Button color='error' variant='contained' onClick={deleteRole}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'r_$1')
    .slice(0, 40);
}
